import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OSCAR_URL, HOST } from "../content";

function starShape() {
  const shape = new THREE.Shape();
  const outer = 0.85, inner = 0.36;
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
    const x = Math.cos(a) * r, y = Math.sin(a) * r;
    if (i === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();
  return shape;
}

export const SealCanvas = ({ hovered }) => {
  const mountRef = useRef(null);
  const stateRef = useRef({ hovered: false, mouse: { x: 0, y: 0 } });
  stateRef.current.hovered = hovered;

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 50);
    camera.position.z = 5;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);

    const group = new THREE.Group();
    const gold = new THREE.MeshStandardMaterial({ color: "#5D8FC4", metalness: 0.85, roughness: 0.28 });
    const torus = new THREE.Mesh(new THREE.TorusGeometry(1.45, 0.2, 32, 96), gold);
    const star = new THREE.Mesh(
      new THREE.ExtrudeGeometry(starShape(), { depth: 0.22, bevelEnabled: true, bevelSize: 0.04, bevelThickness: 0.04, bevelSegments: 3 }),
      gold.clone()
    );
    star.position.z = -0.11;
    group.add(torus, star);
    group.position.y = -4;
    scene.add(group);

    scene.add(new THREE.AmbientLight(0xffffff, 0.35));
    const key = new THREE.PointLight(0xdcebff, 30);
    key.position.set(2.5, 3, 4);
    const rim = new THREE.PointLight(0x5d8fc4, 18);
    rim.position.set(-3, -2, 3);
    scene.add(key, rim);

    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      stateRef.current.mouse.x = ((e.clientX - r.left) / r.width - 0.5) * 2;
      stateRef.current.mouse.y = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    mount.parentElement.addEventListener("mousemove", onMove);

    const resize = () => {
      const w = mount.clientWidth || 1, h = mount.clientHeight || 1;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    let raf;
    const clock = new THREE.Clock();
    const loop = () => {
      const t = clock.getElapsedTime();
      const { hovered: hov, mouse } = stateRef.current;
      const targetY = hov ? 0 : -4;
      group.position.y += (targetY - group.position.y) * 0.14;
      const tiltMax = Math.PI / 12;
      group.rotation.x += ((hov ? mouse.y * tiltMax : 0) - group.rotation.x) * 0.08;
      group.rotation.y += ((hov ? mouse.x * tiltMax : 0) + Math.sin(t * 0.5) * 0.08 - group.rotation.y) * 0.08;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      mount.parentElement && mount.parentElement.removeEventListener("mousemove", onMove);
      renderer.dispose();
      if (renderer.domElement.parentNode) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="absolute inset-0" data-testid="seal-canvas" />;
};

export const HostPortrait = () => {
  const [hovered, setHovered] = useState(false);
  const [is3D, setIs3D] = useState(true);

  useEffect(() => {
    setIs3D(window.matchMedia("(pointer: fine)").matches);
  }, []);

  return (
    <div
      data-testid="host-portrait-panel"
      className={`relative aspect-[4/5] bg-[#0c0c0c] border border-white/10 overflow-hidden ${!is3D ? "pulse-panel" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        src={OSCAR_URL}
        alt={HOST.name}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
        style={{ opacity: hovered && is3D ? 0.12 : 1 }}
        data-testid="host-portrait-photo"
      />
      <span className="corner-mark top-3 left-3 border-t border-l border-r-0 border-b-0" />
      <span className="corner-mark top-3 right-3 border-t border-r border-l-0 border-b-0" />
      <span className="corner-mark bottom-3 left-3 border-b border-l border-r-0 border-t-0" />
      <span className="corner-mark bottom-3 right-3 border-b border-r border-l-0 border-t-0" />

      <div
        className="absolute inset-0 transition-opacity duration-700 pointer-events-none"
        style={{
          opacity: hovered ? 1 : 0,
          background: "radial-gradient(circle at 50% 55%, rgba(93,143,196,0.22) 0%, transparent 62%)",
        }}
      />
      {is3D && (
        <div className="absolute inset-0 transition-opacity duration-500" style={{ opacity: hovered ? 1 : 0 }}>
          <SealCanvas hovered={hovered} />
        </div>
      )}
      <div className={`absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-[#070B11]/90 to-transparent transition-opacity duration-500 ${hovered && is3D ? "opacity-0" : "opacity-100"}`}>
        <p className="font-bold text-sm">{HOST.name}</p>
        <p className="font-mono-c text-[10px] tracking-[0.25em] text-white/40 uppercase mt-1">
          {is3D ? "Hover to reveal" : HOST.company}
        </p>
      </div>
    </div>
  );
};
